import { useMemo, useState } from "react";
import { compareIsoDate, todayIn, zonedParts } from "../../../shared/dates";
import type { Member, Task } from "../../../shared/models";
import { choreMembers, kidsFirst } from "../../../shared/people";
import { useAppUi } from "../../app/uiContext";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf, useMembers, useRewards, useTaskActions, useTasks } from "../../data/queries";
import { Avatar } from "../../ui/Avatar";
import { Icon } from "../../ui/Icon";
import { formatReward } from "../../ui/reward";
import { Sheet } from "../../ui/Sheet";
import { EmptyState, StatusBadge } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { TaskEditor } from "../tasks/TasksWidget";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./family.css";

interface ChoresConfig {
  showBalances: boolean;
}

/** A chore belongs on today's chart if it's due today/overdue/undated, or was finished today. */
function choreState(t: Task, today: string, timeZone: string): "todo" | "done" | "hidden" {
  const doneToday = (iso: string | null) => Boolean(iso && zonedParts(new Date(iso), timeZone).date === today);
  if (t.recurrence) {
    if (doneToday(t.lastCompletedAt)) return "done";
    return !t.dueDate || compareIsoDate(t.dueDate, today) <= 0 ? "todo" : "hidden";
  }
  if (t.completedAt) return doneToday(t.completedAt) ? "done" : "hidden";
  return !t.dueDate || compareIsoDate(t.dueDate, today) <= 0 ? "todo" : "hidden";
}

function Chores({ config, editing, openFullscreen, fullscreen }: WidgetProps<ChoresConfig>) {
  const { timeZone } = useHousehold();
  const now = useNow("minute");
  const today = todayIn(timeZone, now);
  const tasks = useTasks();
  const members = useMembers().data ?? [];
  const rewards = useRewards();
  const actions = useTaskActions();
  const { toast } = useAppUi();
  const state = dataStateOf(tasks, 20 * 60_000);
  const currency = rewards.data?.currency ?? "stars";
  const [who, setWho] = useState<Task | null>(null);
  const [adding, setAdding] = useState<string | null | false>(false);
  const [burst, setBurst] = useState<string | null>(null);

  const people = choreMembers(
    members,
    (tasks.data ?? []).filter((t) => t.kind === "chore").map((t) => t.assigneeId),
  );
  const balance = (id: string) => rewards.data?.balances.find((b) => b.memberId === id)?.balance ?? 0;

  const chores = useMemo(
    () =>
      (tasks.data ?? [])
        .filter((t) => t.kind === "chore")
        .map((t) => ({ t, s: choreState(t, today, timeZone) }))
        .filter((x) => x.s !== "hidden"),
    [tasks.data, today, timeZone],
  );
  const unassigned = chores.filter((c) => !c.t.assigneeId || !people.some((p) => p.id === c.t.assigneeId));

  const complete = (t: Task, memberId?: string) => {
    const earner = members.find((m) => m.id === (memberId ?? t.assigneeId));
    actions.complete.mutate(
      { task: t, memberId },
      {
        onSuccess: (updated) => {
          setBurst(t.id);
          window.setTimeout(() => setBurst(null), 1200);
          toast(t.reward > 0 && earner ? `${earner.displayName} earned ${formatReward(t.reward, currency)}!` : `“${t.title}” done`, {
            label: "Undo",
            run: () => actions.reopen.mutate(updated),
          });
        },
        onError: (e) => toast(`Couldn’t check off “${t.title}”: ${(e as Error).message}`),
      },
    );
  };

  const tap = (t: Task, s: "todo" | "done") => {
    if (editing) return;
    if (s === "done") return actions.reopen.mutate(t);
    if (!t.assigneeId && t.reward > 0) return setWho(t);
    complete(t);
  };

  const Item = ({ t, s }: { t: Task; s: "todo" | "done" }) => (
    <li>
      <button className={`chore ${s} ${burst === t.id ? "burst" : ""}`} onClick={() => tap(t, s)} aria-pressed={s === "done"}>
        <span className="chore-check">{s === "done" ? <Icon name="check" size="1.1rem" /> : null}</span>
        <span className="chore-title">{t.title}</span>
        {t.reward > 0 && <span className="chore-reward">+{formatReward(t.reward, currency)}</span>}
        {burst === t.id && <span className="chore-burst" aria-hidden="true" />}
      </button>
    </li>
  );

  return (
    <div className="chores">
      <WidgetHeader
        title="Chore chart"
        meta={currency === "dollars" ? "Earning dollars" : "Earning stars"}
        onTitle={fullscreen ? undefined : openFullscreen}
        actions={
          <button className="icon-btn icon-btn-accent" onClick={() => setAdding(null)} aria-label="Add chore" disabled={editing}>
            <Icon name="plus" />
          </button>
        }
      />
      {!tasks.data ? (
        state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="tasks" title="Chores unavailable" detail={tasks.error?.message} />
      ) : people.length === 0 ? (
        <EmptyState icon="user" title="Add your family first" detail="Children get their own column on the chore chart." />
      ) : (
        <div className="chore-cols" style={{ gridTemplateColumns: `repeat(${people.length + (unassigned.length ? 1 : 0)}, minmax(0, 1fr))` }}>
          {people.map((m) => {
            const mine = chores.filter((c) => c.t.assigneeId === m.id);
            const left = mine.filter((c) => c.s === "todo").length;
            return (
              <section key={m.id} className="chore-col" style={{ ["--member" as string]: m.color }}>
                <header className="chore-who">
                  <Avatar member={m} size="2.6rem" />
                  <span className="chore-name">
                    <strong>{m.displayName}</strong>
                    <span className="muted small">{mine.length === 0 ? "No chores today" : left === 0 ? "All done! 🎉" : `${left} to go`}</span>
                  </span>
                  {config.showBalances && <span className="chore-balance">{formatReward(balance(m.id), currency)}</span>}
                </header>
                <ul className="chore-list">
                  {mine.map((c) => (
                    <Item key={c.t.id} t={c.t} s={c.s as "todo" | "done"} />
                  ))}
                </ul>
                <button className="chore-add" onClick={() => setAdding(m.id)} disabled={editing}>
                  <Icon name="plus" size="1rem" /> Add for {m.displayName}
                </button>
              </section>
            );
          })}
          {unassigned.length > 0 && (
            <section className="chore-col anyone">
              <header className="chore-who">
                <span className="chore-anyone-icon">
                  <Icon name="user" />
                </span>
                <span className="chore-name">
                  <strong>Anyone</strong>
                  <span className="muted small">Whoever does it earns it</span>
                </span>
              </header>
              <ul className="chore-list">
                {unassigned.map((c) => (
                  <Item key={c.t.id} t={c.t} s={c.s as "todo" | "done"} />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
      <StatusBadge state={state} updatedAt={tasks.dataUpdatedAt} />
      {who && <WhoDidIt task={who} people={kidsFirst(members)} onPick={(m) => (complete(who, m.id), setWho(null))} onClose={() => setWho(null)} />}
      {adding !== false && <TaskEditor task={null} kind="chore" defaultRepeat="daily" defaultAssignee={adding} members={members} today={today} onClose={() => setAdding(false)} />}
    </div>
  );
}

function WhoDidIt({ task, people, onPick, onClose }: { task: Task; people: Member[]; onPick: (m: Member) => void; onClose: () => void }) {
  return (
    <Sheet title={`Who did “${task.title}”?`} onClose={onClose} size="sm">
      <div className="who-picker big">
        {people.map((m) => (
          <button key={m.id} className="who" style={{ ["--member" as string]: m.color }} onClick={() => onPick(m)}>
            <Avatar member={m} size="3.6rem" />
            <span>{m.displayName}</span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

export const choresWidget: WidgetDefinition<ChoresConfig> = {
  type: "chores",
  version: 1,
  name: "Chore chart",
  description: "Each child’s chores for today. Tap to check off and earn stars or dollars.",
  icon: "tasks",
  category: "household",
  defaultSize: { w: 8, h: 6 },
  minSize: { w: 3, h: 3 },
  stackHeight: 480,
  defaults: { showBalances: true },
  settings: [{ key: "showBalances", label: "Show balances", type: "toggle" }],
  provider: "kinmantle-tasks",
  supportsFullscreen: true,
  Component: Chores,
};
